import { InputField } from '@/components/form/InputField';
import { PhoneInputField } from '@/components/form/PhoneInputField';
import { SelectField } from '@/components/form/SelectField';
import { Form } from '@/components/primitives/Form';
import { getCountries } from '@/features/configuration/configurationActions';
import { countriesSelector } from '@/features/configuration/configurationSelector';
import { updateProfileLoadingSelector } from '@/features/loading/loadingSelectors';
import { updateProfile } from '@/features/profile/profileActions';
import { profileDataSelector } from '@/features/profile/profileSelectors';
import { useTranslation } from '@/i18n';
import { TransText } from '@/i18n/trans/text';
import { useDispatch, useSelector } from '@/store/utils';
import type { ApiErrorResponse } from '@/types/api';
import {
  GenderOptions,
  type UpdateCustomerProfileRequest,
} from '@/types/profile';
import { showErrorToast } from '@/utils/errorToast';
import { Button, Divider, Icons, Typography } from '@fleet/webshop-ui';
import { useCallback, useEffect, useMemo, useState, type FC } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

interface MyInformationProps {}

const MyInformation: FC<MyInformationProps> = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const profile = useSelector(profileDataSelector);
  const countries = useSelector(countriesSelector);
  const isUpdatingProfile = useSelector(updateProfileLoadingSelector);
  const [isLoadingCountries, setIsLoadingCountries] = useState(true);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        await dispatch(getCountries()).unwrap();
      } catch (error) {
        console.error('Failed to fetch countries:', error);
      } finally {
        setIsLoadingCountries(false);
      }
    };

    if (!countries) {
      fetchCountries();
    } else {
      setIsLoadingCountries(false);
    }
  }, [dispatch, countries]);

  // Convert profile data to form values
  const defaultValues = useMemo<UpdateCustomerProfileRequest>(
    () => ({
      firstName: profile?.firstName ?? '',
      lastName: profile?.lastName ?? '',
      phoneNumber: profile?.phoneNumber,
      birthDate: profile?.birthDate,
      genderId: profile?.gender?.id,
      countryId: profile?.country?.id,
      streetAddress: profile?.streetAddress,
      city: profile?.city,
      zipCode: profile?.zipCode,
      dietaryRequirementIds: [],
      seatPreferenceIds: [],
    }),
    [profile]
  );

  const form = useForm<UpdateCustomerProfileRequest>({
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    defaultValues,
  });

  const { reset, formState } = form;

  const onSubmit = useCallback(
    async (data: UpdateCustomerProfileRequest) => {
      try {
        await dispatch(updateProfile(data)).unwrap();
        toast.success(<TransText i18nKey="yourInformationHasBeenUpdated" />);
      } catch (e: unknown) {
        showErrorToast(
          <TransText i18nKey="updatingInformationFailed" />,
          e as ApiErrorResponse
        );
      }
    },
    [dispatch]
  );

  const handleRevert = useCallback(() => {
    reset(defaultValues);
  }, [reset, defaultValues]);

  const countryOptions = useMemo(
    () =>
      countries?.items?.map((country) => ({
        value: country.id ?? '',
        label: country.name ?? '',
      })) ?? [],
    [countries]
  );

  const genderOptions = useMemo(() => {
    const genderMap: Record<GenderOptions, string> = {
      [GenderOptions.MALE]: t('text.male'),
      [GenderOptions.FEMALE]: t('text.female'),
      [GenderOptions.X]: t('text.other'),
    };

    return Object.values(GenderOptions).map((value) => ({
      value,
      label: genderMap[value],
    }));
  }, [t]);

  return (
    <div className="flex h-fit w-full text-dark">
      <div className="w-full rounded-lg bg-white px-6 pb-6 pt-4">
        <Form
          form={form}
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-4"
        >
          <div className="flex items-center gap-3">
            <Icons.user className="h-6 w-6" />
            <Typography variant="heading2">
              <TransText i18nKey="myInformation" />
            </Typography>
          </div>

          <Divider className="mt-4" />

          <div className="flex w-full flex-col gap-6 laptop:flex-row laptop:gap-6">
            <div className="flex w-full flex-1 flex-col gap-4">
              <Typography variant="body1-bold">
                <TransText i18nKey="requiredData" />
              </Typography>
              <InputField
                name="firstName"
                label={<TransText i18nKey="firstName" />}
                required
              />
              <InputField
                name="lastName"
                label={<TransText i18nKey="lastName" />}
                required
              />
            </div>

            <Divider
              orientation="vertical"
              grow={false}
              className="hidden h-auto laptop:block"
            />
            <Divider className="laptop:hidden" />

            <div className="flex w-full flex-1 flex-col gap-4">
              <Typography variant="body1-bold">
                <TransText i18nKey="optionalData" />
              </Typography>
              <PhoneInputField
                name="phoneNumber"
                label={<TransText i18nKey="phoneNumber" />}
              />
              <SelectField
                name="countryId"
                label={<TransText i18nKey="nationality" />}
                options={countryOptions}
                placeholder={
                  isLoadingCountries
                    ? t('text.loading')
                    : t('text.selectCountry')
                }
                classNames={{ select: 'bg-white' }}
              />
              <InputField
                name="birthDate"
                type="date"
                label={<TransText i18nKey="birthDate" />}
                classNames={{ input: 'bg-white' }}
              />
              <SelectField
                name="genderId"
                label={<TransText i18nKey="gender" />}
                options={genderOptions}
                placeholder={t('text.selectGender')}
                classNames={{ select: 'bg-white' }}
              />
              <InputField
                name="streetAddress"
                label={<TransText i18nKey="streetName" />}
              />
            </div>
          </div>

          <div className="flex w-full gap-4">
            <Button
              type="button"
              variant="tertiary"
              onClick={handleRevert}
              isDisabled={!formState.isDirty}
              data-testid="revert-changes-button"
            >
              <TransText i18nKey="revertChanges" />
            </Button>
            <Button
              type="submit"
              variant="secondary"
              className="gap-2"
              data-testid="save-profile-button"
              isPending={isUpdatingProfile}
              startAdornment={<Icons.check className="h-5 w-5" />}
            >
              <TransText i18nKey="save" />
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default MyInformation;
